import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Edit, Trash2, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';
import { MarkdownContent } from '../components/MarkdownContent';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATS = ['General', 'Dental Health', 'Medical Tips', 'Wellness', 'News'];

const PostEditor = ({ data, onChange, onSubmit, loading, label }) => (
  <form onSubmit={onSubmit} className="space-y-4 mt-4" data-testid="edit-post-form">
    <div className="space-y-2">
      <Label>Title *</Label>
      <Input required value={data.title} onChange={e => onChange({...data, title: e.target.value})} data-testid="edit-input-title" />
    </div>
    <div className="space-y-2">
      <Label>Excerpt *</Label>
      <Input required value={data.excerpt} onChange={e => onChange({...data, excerpt: e.target.value})} data-testid="edit-input-excerpt" />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>Category</Label>
        <select value={data.category} onChange={e => onChange({...data, category: e.target.value})} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="edit-input-category">
          <option value="General">General</option>
          <option value="Dental Health">Dental Health</option>
          <option value="Medical Tips">Medical Tips</option>
          <option value="Wellness">Wellness</option>
          <option value="News">News</option>
        </select>
      </div>
      <div className="space-y-2">
        <Label>Tags (comma-separated)</Label>
        <Input value={data.tags} onChange={e => onChange({...data, tags: e.target.value})} data-testid="edit-input-tags" />
      </div>
    </div>
    <div className="space-y-2">
      <Label>Featured Image URL</Label>
      <Input value={data.image_url} onChange={e => onChange({...data, image_url: e.target.value})} data-testid="edit-input-image" />
    </div>
    <div className="space-y-2">
      <Label>Content * (Markdown supported)</Label>
      <Textarea required value={data.content} onChange={e => onChange({...data, content: e.target.value})} rows={12} className="font-mono text-sm" data-testid="edit-input-content" />
    </div>
    <div className="flex items-center gap-2">
      <input type="checkbox" id="pub" checked={data.published} onChange={e => onChange({...data, published: e.target.checked})} className="rounded" />
      <Label htmlFor="pub" className="text-sm">Published</Label>
    </div>
    <Button type="submit" disabled={loading} className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full" data-testid="save-edit-btn">
      {loading ? 'Saving...' : label}
    </Button>
  </form>
);

export const BlogPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin] = useState(() => !!sessionStorage.getItem('blog_admin'));
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editData, setEditData] = useState({ title: '', excerpt: '', content: '', author: '', image_url: '', category: 'General', tags: '', published: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/blog/${id}`);
        setPost(res.data);
        setEditData({
          title: res.data.title, excerpt: res.data.excerpt, content: res.data.content,
          author: res.data.author, image_url: res.data.image_url || '',
          category: res.data.category || 'General',
          tags: (res.data.tags || []).join(', '),
          published: res.data.published !== false
        });
      } catch { navigate('/blog'); }
      finally { setLoading(false); }
    };
    load();
  }, [id, navigate]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    const pw = sessionStorage.getItem('blog_admin');
    try {
      const payload = { ...editData, tags: editData.tags ? editData.tags.split(',').map(t => t.trim()).filter(Boolean) : [] };
      await axios.put(`${API}/blog/${id}?password=${encodeURIComponent(pw)}`, payload);
      toast.success('Post updated!');
      setIsEditOpen(false);
      const res = await axios.get(`${API}/blog/${id}`);
      setPost(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Update failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    const pw = sessionStorage.getItem('blog_admin');
    try {
      await axios.delete(`${API}/blog/${id}?password=${encodeURIComponent(pw)}`);
      toast.success('Post deleted!');
      navigate('/blog');
    } catch (err) { toast.error(err.response?.data?.detail || 'Delete failed'); }
  };

  const fmtDate = (d) => new Date(d).toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' });

  if (loading) return (
    <div className="min-h-screen bg-slate-50 py-16">
      <div className="max-w-4xl mx-auto px-4 animate-pulse">
        <div className="h-8 bg-slate-200 rounded w-1/4 mb-8" />
        <div className="h-64 bg-slate-200 rounded-2xl mb-8" />
        <div className="h-8 bg-slate-200 rounded w-3/4 mb-4" />
        <div className="h-4 bg-slate-200 rounded w-full mb-4" />
        <div className="h-4 bg-slate-200 rounded w-3/4" />
      </div>
    </div>
  );

  if (!post) return (
    <div className="min-h-screen bg-slate-50 py-16 text-center">
      <h1 className="text-2xl font-bold text-slate-900 mb-4">Post Not Found</h1>
      <Button asChild className="bg-teal-600 hover:bg-teal-700 text-white rounded-full"><Link to="/blog">Back to Blog</Link></Button>
    </div>
  );

  const tagList = post.tags || [];

  return (
    <div className="min-h-screen" data-testid="blog-post-page">
      <SEO title={post.title} description={post.excerpt} keywords={post.category} />

      <section className="bg-gradient-to-br from-slate-50 to-teal-50/30 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/blog" className="inline-flex items-center gap-2 text-teal-600 font-medium hover:gap-3 transition-all mb-6" data-testid="back-to-blog-btn">
            <ArrowLeft className="w-5 h-5" /> Back to Blog
          </Link>
        </div>
      </section>

      <section className="py-8 lg:py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          {post.image_url && (
            <div className="rounded-2xl overflow-hidden shadow-lg mb-8">
              <img src={post.image_url} alt={post.title} className="w-full h-[300px] sm:h-[400px] object-cover" />
            </div>
          )}

          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4 text-slate-500 text-sm">
              <span className="flex items-center gap-2"><User className="w-4 h-4" /> {post.author}</span>
              <span className="flex items-center gap-2"><Calendar className="w-4 h-4" /> {fmtDate(post.created_at)}</span>
              <span className="px-3 py-1 rounded-full bg-teal-100 text-teal-700 text-xs font-medium">{post.category || 'General'}</span>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2" data-testid="admin-actions">
                <Button variant="outline" size="sm" className="rounded-full" onClick={() => setIsEditOpen(true)} data-testid="edit-post-btn">
                  <Edit className="w-4 h-4 mr-2" /> Edit
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-post-btn">
                      <Trash2 className="w-4 h-4 mr-2" /> Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Blog Post</AlertDialogTitle>
                      <AlertDialogDescription>Are you sure? This cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-btn">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-4">{post.title}</h1>

          {tagList.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-8">
              {tagList.map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-medium">
                  <Tag className="w-3 h-3" /> {tag}
                </span>
              ))}
            </div>
          )}

          <div className="prose prose-lg max-w-none" data-testid="blog-content">
            <MarkdownContent content={post.content} />
          </div>

          <Card className="mt-12 border-0 shadow-lg rounded-2xl bg-gradient-to-br from-teal-600 to-teal-700">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold text-white mb-4">Need Medical Advice?</h3>
              <p className="text-teal-100 mb-6">Our team is ready to help.</p>
              <Button asChild className="bg-white text-teal-700 hover:bg-slate-100 rounded-full px-8 shadow-lg" data-testid="post-contact-btn">
                <Link to="/contact">Contact Us</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Blog Post</DialogTitle></DialogHeader>
          <PostEditor data={editData} onChange={setEditData} onSubmit={handleUpdate} loading={saving} label="Save Changes" />
        </DialogContent>
      </Dialog>
    </div>
  );
};
